import { Component, ElementRef, HostListener, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ProjectService } from '.././services/project.service';
import { filter, Subject, Subscription, takeUntil, timer } from 'rxjs';
import { TemperatureDataRow } from '.././models/temperature-data-row';
import { ProjectTemperatureData } from '.././models/project-temperature-data';
import { Router, ActivatedRoute } from '@angular/router';
import { environment } from 'src/environments/environment';

// Required for MSAL
import {
  MsalService,
  MsalBroadcastService,
  MSAL_GUARD_CONFIG,
  MsalGuardConfiguration,
} from '@azure/msal-angular';
import {
  AccountInfo,
  EventMessage,
  EventType,
  InteractionStatus,
  RedirectRequest,
} from '@azure/msal-browser';
import { UserAuthService } from '../UserAuth.service';
import { ProjectInfo } from '../models/project-info';
import { DeviceStatus } from '../services/project.service';
import { PreferencesService } from '../services/preferences.service';

@Component({
  selector: 'app-main-layout',
  templateUrl: './main-layout.component.html',
  styleUrls: ['./main-layout.component.scss'],
})
export class MainLayoutComponent implements OnInit, OnDestroy {
  title = 'vericure-webui';
  showHeader = false;
  selectedProject: string = '';
  uploadedroject: string = '';
  isRunning: boolean = false;
  tick: number = 0;
  tickMax: number = 0;
  tempDataLoading: Boolean = false;
  zoneDataLoading: Boolean = false;
  lossDataLoading: boolean = false;
  lossDataLoaded: boolean = false;
  notesDataLoading: boolean = false;
  projUploading: boolean = false;
  projectLoadPerc: number = 0;
  fiberFaultLevel: number = 0; // 0: Normal, 1: Warning, 2: Error
  cmsFaultLevel: number = 0; // 0: Normal, 1: Warning, 2: Error
  deviceStatus: DeviceStatus = 'unknown';
  private readonly _destroying$ = new Subject<void>();

  projTemperatureData: ProjectTemperatureData = {
    TRC: [],
    SI: 0,
    MI: 0,
    CRB: 0,
    CRE: 0,
    CRS: 0,
    LSS: 0,
    LES: 0,
    CT: 0,
    OT: 0,
    SER: '',
    FIB: '',
    HMC: [],
    MAR: [],
    N: [],
    U: [],
    LG: '',
    VS: 0,
  };
  currTime: Date = new Date(0, 0, 0);
  currDuration: number = 0;

  uploadedProjectSubscription: Subscription = {} as Subscription;
  fiberFaultLevelSubscription: Subscription = {} as Subscription;
  cmsFaultLevelSubscription: Subscription = {} as Subscription;

  lastEpocTime: number = 0;
  fetchRealTimeData: boolean = false;

  isAuthenticated = false;
  isAdmin = false;
  isSuperAdmin = false;
  activeAccount: AccountInfo | null = null;
  activeCustomerName = '';
  
  // Modal properties
  showProjectModal = false;
  activeTab = 'existing';
  isModalMaximized = false;
  isModalMinimized = false;
  isModalResizing = false;
  modalStartX = 0;
  modalStartY = 0;
  modalStartWidth = 0;
  modalStartHeight = 0;
  
  userDropdownOpen = false;
  @ViewChild('userMenuRef') userMenuRef!: ElementRef;

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.userMenuRef && !this.userMenuRef.nativeElement.contains(event.target)) {
      this.userDropdownOpen = false;
    }
  }

  // Export panel properties
  showExportPanel = false;
  exportPanelMode: 'pdf' | 'csv' = 'pdf';

  // Search and filter properties
  searchTerm = '';
  selectedFilter = 'all'; // 'all', 'live', 'historical'
  dateFilterFrom: string = '';
  dateFilterTo: string = '';
  showAdvancedFilters = false;

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    public authService: UserAuthService,
    public projectService: ProjectService,
    private preferencesService: PreferencesService,
    private _router: Router,
    private activatedRoute: ActivatedRoute,
    private msalBroadcastService: MsalBroadcastService
  ) {}

  ngOnDestroy(): void {
    this.uploadedProjectSubscription.unsubscribe();
    this.fiberFaultLevelSubscription.unsubscribe();
    this.cmsFaultLevelSubscription.unsubscribe();
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  onProjectChange(proj: string) {
    console.log('app comp > Proj Change Called: ' + proj);
    
    // Always use the current modal settings (radio button selection)
    const selectedUnit = this.projectService.toUpload_isMetricUnit;
    const selectedLang = this.projectService.toUpload_selectedLang;
    
    // Sync both unit properties
    this.projectService.isMetricUnit = selectedUnit;
    this.projectService.toUpload_isMetricUnit = selectedUnit;
    this.projectService.selectedLang = selectedLang;
    this.projectService.toUpload_selectedLang = selectedLang;
    
    // Save these settings for this project
    this.projectService.setProjectUnitPreference(proj, selectedUnit);
    this.projectService.setProjectLanguagePreference(proj, selectedLang);
    
    console.log('Project unit setting:', this.projectService.isMetricUnit ? 'Metric' : 'Imperial');
    console.log('Project language setting:', this.projectService.selectedLang);
    console.log('Unit code for API:', this.projectService.isMetricUnit ? '0 (Metric)' : '1 (Imperial)');

    this.projectService.setFiberFaultLevel(0);
    this.projectService.resetDeviceStatus();

    this.fetchRealTimeData = false;
    this.lastEpocTime = 0;
    this.tempDataLoading = proj.length > 0;
    this.zoneDataLoading = false;
    this.lossDataLoading = false;
    this.lossDataLoaded = false;
    this.notesDataLoading = false;
    this.projectLoadPerc = 0;
    this._router.navigateByUrl('/temperature');
    this.selectedProject = proj;
    this.projectService.setProject(proj);
    
    // Save the selected project to localStorage for persistence across refreshes
    if (proj && proj.length > 0) {
      localStorage.setItem('lastSelectedProject', proj);
      console.log('Main Layout > Saved project to localStorage:', proj);
    }
    
    //this.projectService.loadTemperatureData(proj);
    this.projectService.loadTemperatureData(proj, false, 0).subscribe(
      (data) => {
        console.log('app comp > projectService.loadTemperatureData.subscribe');
        this.projectService.setTempData(data);
        // Extract and set notes data from temperature data response
        if (data.N) {
          this.projectService.setNotesData(data.N);
        }
        // Extract and set markers data from temperature data response
        if (data.MAR) {
          console.log('MainLayout > Loading markers from temperature data:', data.MAR);
          this.projectService.loadMarkers(data.MAR);
        } else {
          console.log('MainLayout > No MAR data in temperature response');
        }
      },
      (error) => console.log(error)
    );
  }

  ngOnInit(): void {
    console.log('Prod : ' + environment.production);
    this.showHeader = !environment.production;

    // Check for query parameter to open projects modal
    this.activatedRoute.queryParams.subscribe(params => {
      if (params['openProjects'] === 'true') {
        console.log('Main Layout > openProjects query parameter detected - opening project modal');
        // Open the modal after a short delay to ensure the component is fully initialized
        setTimeout(() => {
          this.openProjectModal();
          // Clear the query parameter after opening the modal
          this._router.navigate(['/'], { replaceUrl: true });
        }, 500);
      }
    });

    // Wait for MSAL to finish initialization before proceeding
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        console.log('Main Layout > MSAL initialization complete');
        this.setupAuthentication();
      });
  }

  private setupAuthentication(): void {
    // Subscribe to role observables for Admin entry-point visibility
    this.authService.isAdmin$.subscribe((admin) => (this.isAdmin = admin));
    this.authService.isSuperAdmin$.subscribe((sa) => (this.isSuperAdmin = sa));
    this.authService.activeCustomer$.subscribe((c) => (this.activeCustomerName = c?.CustomerName ?? ''));

    // Subscribe to Authentication status
    this.authService.isAuthenticated$.subscribe((isAuthenticated) => {
      this.isAuthenticated = isAuthenticated;
      if (isAuthenticated) {
        this.preferencesService.load();
        this.authService.activeAccount$.subscribe((account) => {
          this.activeAccount = account;
          
          if (this.activeAccount?.localAccountId) {
            // MSAL authentication - use MSAL account data
            const CustomerTenantId = this.authService.getCurrentCustomerTenantId();
            console.log('Main Layout > Customer Tenant ID:', CustomerTenantId);
            
            // Pass the customer tenant ID to the project service
            this.projectService.loadProjectList(this.activeAccount.localAccountId, CustomerTenantId);
            this.initializeProjects();
            
            // Check and restore last selected project after project list loads
            this.restoreLastSelectedProject();
          } else {
            // Email/password authentication - check localStorage
            const accessToken = localStorage.getItem('accessToken');
            const username = localStorage.getItem('username');
            const customerTenantId = localStorage.getItem('customerTenantId');
            
            if (accessToken && username) {
              console.log('Main Layout > Email/password authentication detected');
              console.log('Main Layout > Customer Tenant ID from localStorage:', customerTenantId);
              
              // Prefer the selected customer's tenant; fall back to the login tenant.
              const effectiveTenantId = this.authService.getCurrentCustomerTenantId() || customerTenantId || '';
              this.projectService.loadProjectList(username, effectiveTenantId);
              this.initializeProjects();
              
              // Check and restore last selected project after project list loads
              this.restoreLastSelectedProject();
            }
          }
        });
      } else {
        this.activeAccount = null;
      }
    });
  }

  initializeProjects() {
    this.isRunning = false;
    timer(0, 100).subscribe((ellapsedCycles) => {
      //console.log('timer');
      if (this.isRunning) {
        //console.log('app comp > time is running');

        if (this.tick < this.tickMax) {
          this.tick += 1;
          var strTick = this.tick.toString();
          this.updateChartDataForTick(strTick);
        } else {
          this.isRunning = false;
        }
      }
    });

    timer(0, 5 * 1000).subscribe((ellapsedCycles) => {
      console.log('app comp > Fetching Realtime Data...', {
        selectedProject: this.selectedProject,
        fetchRealTimeData: this.fetchRealTimeData,
        showProjectModal: this.showProjectModal,
        tempDataLoading: this.tempDataLoading,
        zoneDataLoading: this.zoneDataLoading,
        lossDataLoading: this.lossDataLoading,
        lastEpocTime: this.lastEpocTime,
        ellapsedCycles: ellapsedCycles
      });

      if (this.selectedProject.length > 0 && this.fetchRealTimeData == true && !this.showProjectModal && !this.tempDataLoading && !this.zoneDataLoading && !this.lossDataLoading) {
        // Fetch Temp Data
        this.projectService
          .loadTemperatureData(this.selectedProject, true, this.lastEpocTime)
          .subscribe(
            (data) => {
              if (data.TRC.length > 0) {
                console.log('app comp > Realtime response received');
                this.projectService.setDeviceOnline();
                this.projectService.setRTTempData(data);
                this.lastEpocTime = data.TRC[0].E;
                this.currTime = data.TRC[0].T;
                console.log(
                  'Epoc Time: ' + this.lastEpocTime + ' = ' + this.currTime
                );
                this.tick += 1;
                //this.projTemperatureData = data;
                this.currDuration = this.projTemperatureData.MI * this.tick;
              } else {
                console.log('app comp > No Realtime data');
                this.projectService.checkDeviceTimeout();
              }
              
              // Extract and set notes data from real-time temperature data response
              if (data.N) {
                this.projectService.setNotesData(data.N);
              }
              // Extract and set markers data from real-time temperature data response
              if (data.MAR) {
                console.log('MainLayout > Loading markers from real-time temperature data:', data.MAR);
                this.projectService.loadMarkers(data.MAR);
              }
            },
            (error) => {
              console.log(error);
              this.projectService.checkDeviceTimeout();
            }
          );

          // Notes don't need continuous real-time updates like temperature data
          // They will be loaded when project is selected and when notes are saved
          // Fetch Zone Data
        console.log('app comp > Fetching Zone RT Data...');
        this.projectService.loadZoneDataRT(
          this.selectedProject,
          this.lastEpocTime
        );

        // Fetch Loss Data
        console.log('app comp > Fetching Loss RT Data...');
        this.projectService.loadLossRTData(
          this.selectedProject,
          this.lastEpocTime
        );
      }
    });

    this.projectService.temperatureData.subscribe((data) => {
      this.tempDataLoading = false;
      this.projTemperatureData = data;
      this.projectLoadPerc = 30;

      if (data == null) {
        console.log('Error in App Comp: Temp data is null.');
        return;
      } else if (data.TRC == null) {
        console.log('Error in App Comp: Temp data > TRC is null.');
        return;
      }
      this.tickMax = data.TRC.length - 1;
      this.tick = this.tickMax;

      if (this.selectedProject.length > 0) {
        this.zoneDataLoading = true;
        //console.log('tempData subscription handler. loading data for zone for proj: ' + this.selectedProject);
        this.projectService.loadZoneData(this.selectedProject);
      } else if (this.uploadedroject.length > 0) {
        this.zoneDataLoading = true;
        //console.log('tempData subscription handler. loading data for zone for proj: ' + this.selectedProject);
        this.projectService.loadZoneData(this.uploadedroject);
      }

      //this.updateChartDataForTick('0');
    });

    this.projectService.zoneData.subscribe((data) => {
      this.tempDataLoading = false;
      this.zoneDataLoading = false;
      this.projectLoadPerc = 60;
      if (this.selectedProject.length > 0) {
        this.lossDataLoading = true;
        //console.log('tempData subscription handler. loading data for zone for proj: ' + this.selectedProject);
        this.projectService.loadLossData(this.selectedProject);
      } else if (this.uploadedroject.length > 0) {
        this.lossDataLoading = true;
        //console.log('tempData subscription handler. loading data for zone for proj: ' + this.selectedProject);
        this.projectService.loadLossData(this.uploadedroject);
      }

      //this.tickMax = data.ZRC.length - 1;
    });

    this.projectService.lossData.subscribe((data) => {
      this.lossDataLoading = false;
      var validLossData = true;
      this.projectLoadPerc = 90;

      if (data == null || data === undefined) {
        validLossData = false;
      } else if (data.LRC == null) {
        validLossData = false;
      } else if (data.LRC.length == 0) {
        validLossData = false;
      }
      //this.setTickToEnd();
      this.fetchRealTimeData = true;

      if (validLossData) {
        const lossDataRows = data.LRC;
        var tickCount = data.LRC.length - 1;
        // Update Faults
        if (lossDataRows[tickCount].FD.length > 0) {
          var FD = lossDataRows[tickCount].FD;
          if (FD.some((c) => c.FT == '1')) {
            hasCMSFaults = true;
            console.log('has CMS faults 1');
            this.projectService.setFiberFaultLevel(1);
          } else if (FD.some((c) => c.FT == '2')) {
            hasCMSFaults = true;
            console.log('has CMS faults 2');
            this.projectService.setFiberFaultLevel(2);
          }
        }
      }

      if (this.selectedProject.length > 0 || this.uploadedroject.length > 0) {
        //this.notesDataLoading = true;
        //console.log('tempData subscription handler. loading data for zone for proj: ' + this.selectedProject);
        // Notes are now loaded from temperature data response (N property)
        // No need for separate notes API calls
        this.projectLoadPerc = 100;
      }

      var hasCMSFaults = false;
    });

    this.uploadedProjectSubscription =
      this.projectService.uploadedProject.subscribe(
        (projName) => {
          //console.log('app comp > projectService.uploadedProject.subscribe');
          this.onProjectChange(projName);
          this.projectLoadPerc = 10;

          console.log('app comp > loading Temp data');
        },
        (error) => console.log(error)
      );

    this.fiberFaultLevelSubscription =
      this.projectService.fiberFaultLevel.subscribe((level) => {
        this.fiberFaultLevel = level;
      });

    this.cmsFaultLevelSubscription =
      this.projectService.cmsFaultLevel.subscribe((level) => {
        this.cmsFaultLevel = level;
      });

    this.projectService.deviceStatus$.subscribe((status) => {
      this.deviceStatus = status;
    });
  }

  toggleTimer() {
    this.isRunning = !this.isRunning;
  }

  updateChartDataForTick(val: string) {
    const sliderVal: number = +val;
    this.tick = sliderVal;
    //console.log('sliderVal: ' + sliderVal);
    if (this.projTemperatureData) {
      if (this.projTemperatureData.TRC.length > 0) {
        this.currTime = this.projTemperatureData.TRC[sliderVal].T;
        this.currDuration = this.projTemperatureData.MI * this.tick;
        this.projectService.setTick(sliderVal);
      }
    }
  }

  setTickToBeginning() {
    this.tick = 0;
    this.updateChartDataForTick('0');
  }

  setTickToEnd() {
    this.tick = this.tickMax;
    this.updateChartDataForTick(this.tickMax.toString());
  }

  showHealthPage() {
    if (this.selectedProject.length > 0 && !this.lossDataLoaded) {
      this.lossDataLoaded = true;
      this.lossDataLoading = true;
      //console.log('tempData subscription handler. loading data for zone for proj: ' + this.selectedProject);
      this.projectService.loadLossData(this.selectedProject);
    } else {
      //console.log('No proj selected');
      this.lossDataLoading = false;
    }
    this._router.navigateByUrl('health');
  }

  showProjectUploadPage() {
    this._router.navigateByUrl('');
  }

  showFiberFaultColorClass(): string {
    if (this.fiberFaultLevel == 2 || this.cmsFaultLevel == 2) {
      return 'leftnav fiber-error';
    } else if (this.fiberFaultLevel == 1 || this.cmsFaultLevel == 1) {
      return 'leftnav fiber-warn';
    } else {
      return 'leftnav';
    }
  }

  showNewPage() {
    this._router.navigateByUrl('new');
  }

  clearLoadingNotification() {
    this.tempDataLoading = false;
    this.lossDataLoading = false;
    this.zoneDataLoading = false;
  }

  login(): void {
    this.authService.login();
  }

  logout(): void {
    this.authService.logout();
  }

  // Modal methods
  // ── Export panel methods ─────────────────────────────────────────────────

  toggleExportPanel(): void {
    this.showExportPanel = !this.showExportPanel;
    if (this.showExportPanel) {
      this.exportPanelMode = 'pdf';
    }
  }

  setExportMode(mode: 'pdf' | 'csv'): void {
    this.exportPanelMode = mode;
  }

  closeExportPanel(): void {
    this.showExportPanel = false;
  }

  getExportTenantId(): string {
    return this.authService.getCurrentCustomerTenantId() || '';
  }

  // ── Project modal methods ────────────────────────────────────────────────

  openProjectModal(): void {
    this.showProjectModal = true;
    this.activeTab = 'existing';
    this.isModalMaximized = true;
    this.isModalMinimized = false;
    this.resetModalSize();
    
    // If a project is currently selected, load its preferences into the modal controls
    if (this.selectedProject && this.projectService.hasProjectUnitPreference(this.selectedProject)) {
      const savedUnit = this.projectService.getProjectUnitPreference(this.selectedProject);
      const savedLang = this.projectService.getProjectLanguagePreference(this.selectedProject);
      
      // Sync both unit properties
      this.projectService.isMetricUnit = savedUnit;
      this.projectService.toUpload_isMetricUnit = savedUnit;
      this.projectService.selectedLang = savedLang;
      this.projectService.toUpload_selectedLang = savedLang;
      
      console.log('Modal opened - loaded current project preferences:', 
                  this.projectService.toUpload_isMetricUnit ? 'Metric' : 'Imperial');
    }
  }

  closeProjectModal(reason?: string): void {
    console.log('Modal closing. Reason:', reason || 'Unknown');
    this.showProjectModal = false;
    this.isModalMaximized = false;
    this.isModalMinimized = false;
    this.resetModalSize();
  }

  setActiveTab(tab: string): void {
    this.activeTab = tab;
  }

  onSelectProject(project: ProjectInfo): void {
    this.onProjectChange(project.ProjectName);
    this.closeProjectModal('Project selected');
  }

  getLiveProjects(): ProjectInfo[] {
    // Filter projects that have telemetry within the last 6 hours
    const sixHoursAgo = new Date();
    sixHoursAgo.setHours(sixHoursAgo.getHours() - 6);
    
    console.log('getLiveProjects > Six hours ago:', sixHoursAgo);
    console.log('getLiveProjects > All projects:', this.projectService.projectList);
    
    return this.projectService.projectList
      .filter(project => {
        console.log(`getLiveProjects > Checking project: ${project.ProjectName}`);
        console.log(`  - LastTelemetryReceived: "${project.LastTelemetryReceived}"`);
        console.log(`  - LTR: "${project.LTR}"`);
        
        if (!project.LastTelemetryReceived) {
          console.log(`  - FILTERED OUT: No LastTelemetryReceived`);
          return false;
        }
        
        const lastTelemetryDate = new Date(project.LastTelemetryReceived);
        console.log(`  - Parsed date:`, lastTelemetryDate);
        console.log(`  - Is valid date:`, !isNaN(lastTelemetryDate.getTime()));
        
        if (isNaN(lastTelemetryDate.getTime())) {
          console.log(`  - FILTERED OUT: Invalid date`);
          return false;
        }
        
        const isLive = lastTelemetryDate >= sixHoursAgo;
        console.log(`  - Is live (>= 6 hours ago):`, isLive);
        
        if (!isLive) {
          console.log(`  - FILTERED OUT: Too old`);
        } else {
          console.log(`  - INCLUDED as live project`);
        }
        
        return isLive;
      })
      .sort((a, b) => {
        if (!a.LastTelemetryReceived && !b.LastTelemetryReceived) return 0;
        if (!a.LastTelemetryReceived) return 1;
        if (!b.LastTelemetryReceived) return -1;
        
        const dateA = new Date(a.LastTelemetryReceived);
        const dateB = new Date(b.LastTelemetryReceived);
        
        if (isNaN(dateA.getTime()) && isNaN(dateB.getTime())) return 0;
        if (isNaN(dateA.getTime())) return 1;
        if (isNaN(dateB.getTime())) return -1;
        
        return dateB.getTime() - dateA.getTime(); // Descending order (most recent first)
      });
  }

  getHistoricalProjects(): ProjectInfo[] {
    // Filter projects that have telemetry older than 6 hours
    const sixHoursAgo = new Date();
    sixHoursAgo.setHours(sixHoursAgo.getHours() - 6);
    
    return this.projectService.projectList
      .filter(project => {
        if (!project.LastTelemetryReceived) return true; // Treat null dates as historical
        const lastTelemetryDate = new Date(project.LastTelemetryReceived);
        if (isNaN(lastTelemetryDate.getTime())) return true; // Treat invalid dates as historical
        return lastTelemetryDate < sixHoursAgo;
      })
      .sort((a, b) => {
        // Sort by LastTelemetryReceived in descending order (most recent first)
        // Projects with no date go to the end
        if (!a.LastTelemetryReceived && !b.LastTelemetryReceived) return 0;
        if (!a.LastTelemetryReceived) return 1;
        if (!b.LastTelemetryReceived) return -1;
        
        const dateA = new Date(a.LastTelemetryReceived);
        const dateB = new Date(b.LastTelemetryReceived);
        
        if (isNaN(dateA.getTime()) && isNaN(dateB.getTime())) return 0;
        if (isNaN(dateA.getTime())) return 1;
        if (isNaN(dateB.getTime())) return -1;
        
        return dateB.getTime() - dateA.getTime(); // Descending order (most recent first)
      });
  }

  getProjectDate(project: ProjectInfo): string {
    // Parse the LastTelemetryReceived date and format it with time in 24-hour format
    if (!project.LastTelemetryReceived) {
      return '—';
    }
    const date = new Date(project.LastTelemetryReceived);
    if (isNaN(date.getTime())) {
      return '—';
    }
    const dateStr = date.toLocaleDateString();
    const timeStr = date.toLocaleTimeString('en-GB', { 
      hour12: false, 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit' 
    });
    return `${dateStr} ${timeStr}`;
  }

  getProjectSerial(project: ProjectInfo): string {
    // Return the actual serial number from the API
    return project.SerialNumber;
  }

  getProjectCustomerName(project: ProjectInfo): string {
    console.log('MainLayout > getProjectCustomerName:', {
      projectName: project.ProjectName,
      customerName: project.CustomerName,
      hasCustomerName: !!project.CustomerName,
      project: project
    });
    return project.CustomerName || '—';
  }

  // New methods for dynamic columns
  getAllUserParameterNames(): string[] {
    return ProjectInfo.getAllUserParameterNames(this.projectService.projectList);
  }

  getUserParameterValue(project: ProjectInfo, paramName: string): string {
    return project.getUserParameterValue(paramName);
  }

  getProjectFiberType(project: ProjectInfo): string {
    return project.FiberType || '—';
  }

  getProjectVersion(project: ProjectInfo): string {
    return project.Version || '—';
  }

  getUserDisplayName(): string {
    // For MSAL authentication, use the account username
    if (this.activeAccount?.username) {
      return this.activeAccount.username;
    }
    
    // For email/password authentication, use localStorage username
    const localStorageUsername = localStorage.getItem('username');
    if (localStorageUsername) {
      return localStorageUsername;
    }
    
    // Fallback to UserAuthService username
    return this.authService.UserName || 'User';
  }

  // Search and Filter Methods
  getFilteredProjects(): ProjectInfo[] {
    let filteredProjects = this.projectService.projectList;

    // Apply search filter
    if (this.searchTerm.trim()) {
      const searchLower = this.searchTerm.toLowerCase().trim();
      filteredProjects = filteredProjects.filter(project => 
        project.ProjectName.toLowerCase().includes(searchLower) ||
        project.SerialNumber.toLowerCase().includes(searchLower) ||
        project.CustomerName.toLowerCase().includes(searchLower) ||
        project.FiberType.toLowerCase().includes(searchLower) ||
        project.Version.toLowerCase().includes(searchLower) ||
        project.getUserParameterKeys().toLowerCase().includes(searchLower) ||
        project.getUserParameterValues().toLowerCase().includes(searchLower) ||
        project.getUserParametersText().toLowerCase().includes(searchLower)
      );
    }

    // Apply status filter
    if (this.selectedFilter !== 'all') {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      
      filteredProjects = filteredProjects.filter(project => {
        if (!project.LastTelemetryReceived) return false;
        const lastTelemetryDate = new Date(project.LastTelemetryReceived);
        if (isNaN(lastTelemetryDate.getTime())) return false;
        const isLive = lastTelemetryDate >= sevenDaysAgo;
        
        return this.selectedFilter === 'live' ? isLive : !isLive;
      });
    }

    // Apply date range filter
    if (this.dateFilterFrom || this.dateFilterTo) {
      filteredProjects = filteredProjects.filter(project => {
        if (!project.LastTelemetryReceived) return false;
        const projectDate = new Date(project.LastTelemetryReceived);
        if (isNaN(projectDate.getTime())) return false;
        
        if (this.dateFilterFrom) {
          const fromDate = new Date(this.dateFilterFrom);
          if (projectDate < fromDate) return false;
        }
        
        if (this.dateFilterTo) {
          const toDate = new Date(this.dateFilterTo);
          toDate.setHours(23, 59, 59, 999); // Include the entire day
          if (projectDate > toDate) return false;
        }
        
        return true;
      });
    }

    // Sort by most recent telemetry first
    return filteredProjects.sort((a, b) => {
      if (!a.LastTelemetryReceived && !b.LastTelemetryReceived) return 0;
      if (!a.LastTelemetryReceived) return 1;
      if (!b.LastTelemetryReceived) return -1;
      
      const dateA = new Date(a.LastTelemetryReceived);
      const dateB = new Date(b.LastTelemetryReceived);
      
      if (isNaN(dateA.getTime()) && isNaN(dateB.getTime())) return 0;
      if (isNaN(dateA.getTime())) return 1;
      if (isNaN(dateB.getTime())) return -1;
      
      return dateB.getTime() - dateA.getTime();
    });
  }

  getFilteredLiveProjects(): ProjectInfo[] {
    const sixHoursAgo = new Date();
    sixHoursAgo.setHours(sixHoursAgo.getHours() - 6);
    
    return this.getFilteredProjects().filter(project => {
      if (!project.LastTelemetryReceived) return false;
      const lastTelemetryDate = new Date(project.LastTelemetryReceived);
      if (isNaN(lastTelemetryDate.getTime())) return false;
      return lastTelemetryDate >= sixHoursAgo;
    });
  }

  getFilteredHistoricalProjects(): ProjectInfo[] {
    const sixHoursAgo = new Date();
    sixHoursAgo.setHours(sixHoursAgo.getHours() - 6);
    
    return this.getFilteredProjects().filter(project => {
      if (!project.LastTelemetryReceived) return true; // Treat null dates as historical
      const lastTelemetryDate = new Date(project.LastTelemetryReceived);
      if (isNaN(lastTelemetryDate.getTime())) return true; // Treat invalid dates as historical
      return lastTelemetryDate < sixHoursAgo;
    })
    .sort((a, b) => {
      // Sort by LastTelemetryReceived in descending order (most recent first)
      if (!a.LastTelemetryReceived && !b.LastTelemetryReceived) return 0;
      if (!a.LastTelemetryReceived) return 1;
      if (!b.LastTelemetryReceived) return -1;
      
      const dateA = new Date(a.LastTelemetryReceived);
      const dateB = new Date(b.LastTelemetryReceived);
      
      if (isNaN(dateA.getTime()) && isNaN(dateB.getTime())) return 0;
      if (isNaN(dateA.getTime())) return 1;
      if (isNaN(dateB.getTime())) return -1;
      
      return dateB.getTime() - dateA.getTime(); // Descending order (most recent first)
    });
  }

  clearSearch(): void {
    this.searchTerm = '';
  }

  clearFilters(): void {
    this.searchTerm = '';
    this.selectedFilter = 'all';
    this.dateFilterFrom = '';
    this.dateFilterTo = '';
  }

  toggleAdvancedFilters(): void {
    this.showAdvancedFilters = !this.showAdvancedFilters;
  }

  getProjectCount(): { total: number, live: number, historical: number, filtered: number } {
    const total = this.projectService.projectList.length;
    const live = this.getLiveProjects().length;
    const historical = this.getHistoricalProjects().length;
    const filtered = this.getFilteredProjects().length;
    
    console.log('Project counts:', { total, live, historical, filtered });
    console.log('Raw project list:', this.projectService.projectList);
    
    return { total, live, historical, filtered };
  }

  // Modal Control Methods
  toggleMaximizeModal(): void {
    this.isModalMaximized = !this.isModalMaximized;
    if (this.isModalMaximized) {
      this.isModalMinimized = false;
    }
  }

  minimizeModal(): void {
    this.isModalMinimized = !this.isModalMinimized;
    if (this.isModalMinimized) {
      this.isModalMaximized = false;
    }
  }

  onModalMouseDown(event: MouseEvent): void {
    if (this.isModalMaximized) return;
    
    this.isModalResizing = true;
    this.modalStartX = event.clientX;
    this.modalStartY = event.clientY;
    
    const modalElement = event.target as HTMLElement;
    const modalContent = modalElement.closest('.modal-content') as HTMLElement;
    if (modalContent) {
      const rect = modalContent.getBoundingClientRect();
      this.modalStartWidth = rect.width;
      this.modalStartHeight = rect.height;
      
      // Store the resize direction based on the handle class
      const resizeDirection = this.getResizeDirection(modalElement);
      modalContent.setAttribute('data-resize-direction', resizeDirection);
    }
    
    document.addEventListener('mousemove', this.onModalMouseMove.bind(this));
    document.addEventListener('mouseup', this.onModalMouseUp.bind(this));
    
    event.preventDefault();
  }

  private getResizeDirection(element: HTMLElement): string {
    const classList = element.classList;
    if (classList.contains('resize-handle-se')) return 'se';
    if (classList.contains('resize-handle-sw')) return 'sw';
    if (classList.contains('resize-handle-ne')) return 'ne';
    if (classList.contains('resize-handle-nw')) return 'nw';
    if (classList.contains('resize-handle-s')) return 's';
    if (classList.contains('resize-handle-n')) return 'n';
    if (classList.contains('resize-handle-e')) return 'e';
    if (classList.contains('resize-handle-w')) return 'w';
    return 'se'; // default
  }

  onModalMouseMove(event: MouseEvent): void {
    if (!this.isModalResizing || this.isModalMaximized) return;
    
    const deltaX = event.clientX - this.modalStartX;
    const deltaY = event.clientY - this.modalStartY;
    
    const modalContent = document.querySelector('.modal-content') as HTMLElement;
    if (modalContent) {
      const direction = modalContent.getAttribute('data-resize-direction') || 'se';
      let newWidth = this.modalStartWidth;
      let newHeight = this.modalStartHeight;
      
      // Handle horizontal resizing
      if (direction.includes('e')) {
        newWidth = Math.max(400, this.modalStartWidth + deltaX);
      } else if (direction.includes('w')) {
        newWidth = Math.max(400, this.modalStartWidth - deltaX);
      }
      
      // Handle vertical resizing
      if (direction.includes('s')) {
        newHeight = Math.max(300, this.modalStartHeight + deltaY);
      } else if (direction.includes('n')) {
        newHeight = Math.max(300, this.modalStartHeight - deltaY);
      }
      
      modalContent.style.width = `${newWidth}px`;
      modalContent.style.height = `${newHeight}px`;
      modalContent.style.maxWidth = 'none';
      modalContent.style.maxHeight = 'none';
    }
  }

  onModalMouseUp(): void {
    this.isModalResizing = false;
    document.removeEventListener('mousemove', this.onModalMouseMove.bind(this));
    document.removeEventListener('mouseup', this.onModalMouseUp.bind(this));
  }

  onModalKeyDown(event: KeyboardEvent): void {
    // ESC to close
    if (event.key === 'Escape') {
      this.closeProjectModal('ESC key pressed');
    }
    // F11 or F to toggle fullscreen
    else if (event.key === 'F11' || (event.key === 'f' || event.key === 'F')) {
      event.preventDefault();
      this.toggleMaximizeModal();
    }
  }

  resetModalSize(): void {
    const modalContent = document.querySelector('.modal-content') as HTMLElement;
    if (modalContent) {
      modalContent.style.width = '';
      modalContent.style.height = '';
      modalContent.style.maxWidth = '';
      modalContent.style.maxHeight = '';
    }
  }

  // Prevent modal from closing when clicking on interactive elements
  preventModalClose(event: Event): void {
    console.log('Preventing modal close for event:', event.type, event.target);
    event.stopPropagation();
  }

  // ── Customer switcher ────────────────────────────────────────────────────

  getActiveCustomerName(): string {
    return this.authService.getActiveCustomer()?.CustomerName ?? '';
  }

  showCustomerSwitcher(): boolean {
    return (this.isAdmin || this.isSuperAdmin) &&
      this.authService.availableCustomers.length > 1;
  }

  // Above this many companies, use a compact dropdown instead of a quick-pick list.
  get hasManyCustomers(): boolean {
    return this.authService.availableCustomers.length > 5;
  }

  get activeCustomerId(): string {
    return this.authService.getActiveCustomerId();
  }

  onCompanySelect(customerId: string): void {
    const customer = this.authService.availableCustomers.find(
      (c) => c.CustomerId === customerId
    );
    if (customer) {
      this.switchCustomer(customer);
    }
  }

  switchCustomer(customer: import('../models/active-customer').ActiveCustomer): void {
    this.authService.setActiveCustomer(customer);
    this.userDropdownOpen = false;
    // Clear cached project data
    this.projectService.projectList = [];
    this.selectedProject = '';
    localStorage.removeItem('lastSelectedProject');
    // Reload project list for the newly selected customer
    const objectId = this.authService.getObjectId();
    const tenantId = this.authService.getCurrentCustomerTenantId();
    this.projectService.loadProjectList(objectId, tenantId);
    // Reload preferences so company name/defaults reflect the newly selected customer
    this.preferencesService.refresh();
    // Navigate back to root with openProjects so the modal auto-opens once data arrives
    this._router.navigate(['/'], { queryParams: { openProjects: 'true' } });
  }

  // Restore the last selected project from localStorage after page refresh
  private restoreLastSelectedProject(): void {
    // Wait for the project list to be loaded
    setTimeout(() => {
      const lastProject = localStorage.getItem('lastSelectedProject');
      console.log('Main Layout > Attempting to restore last selected project:', lastProject);
      
      if (lastProject && this.projectService.projectList.length > 0) {
        // Check if the project still exists in the project list
        const projectExists = this.projectService.projectList.some(
          p => p.ProjectName === lastProject
        );
        
        if (projectExists) {
          console.log('Main Layout > Restoring project:', lastProject);
          this.onProjectChange(lastProject);
        } else {
          console.log('Main Layout > Last selected project no longer exists, opening project modal');
          localStorage.removeItem('lastSelectedProject');
          this.openProjectModal();
        }
      } else {
        console.log('Main Layout > No project selected, opening project modal');
        // No project was previously selected or project list is empty - show modal
        this.openProjectModal();
      }
    }, 1000); // Give time for the project list to load from API
  }
}
